import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import type {
  CallerLanguage,
  CalleeLanguage,
} from "@nativecall/shared";
import {
  cancelCallSession,
  createCallSession,
  startCallSession,
} from "../lib/api";
import {
  prepareBrowserVoiceClient,
  type BrowserVoiceSession,
  type PreparedBrowserVoiceClient,
} from "../lib/webVoiceClient";

type Role = "caller" | "callee";

type Phase =
  | "idle"
  | "creating"
  | "starting"
  | "connecting"
  | "live"
  | "ending"
  | "error";

type Props = {
  userId: string;
  callerLanguage: CallerLanguage;
  calleeLanguage: CalleeLanguage;
  onClose: () => void;
};

export function TestCallScreen({
  userId,
  callerLanguage,
  calleeLanguage,
  onClose,
}: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [activeRole, setActiveRole] = useState<Role>("caller");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [legStatus, setLegStatus] = useState({ client: "-", pstn: "-" });

  const callerClientRef = useRef<PreparedBrowserVoiceClient | null>(null);
  const calleeClientRef = useRef<PreparedBrowserVoiceClient | null>(null);
  const callerSessionRef = useRef<BrowserVoiceSession | null>(null);
  const calleeSessionRef = useRef<BrowserVoiceSession | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void startTestCall();
    return () => {
      void teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startTestCall() {
    try {
      setPhase("creating");
      setMessage("Creating session…");
      const session = await createCallSession({
        userId,
        destinationNumberE164: "+818000000000",
        callerLanguage,
        calleeLanguage,
        mode: "translator",
        record: false,
      });
      sessionIdRef.current = session.id;
      setSessionId(session.id);

      setPhase("starting");
      setMessage("Starting mock call (no Twilio dial)…");
      await startCallSession(userId, session.id, {
        mock: true,
        stack: "split",
      });

      setPhase("connecting");
      setMessage("Opening mic & both legs…");
      const callerClient = await prepareBrowserVoiceClient({
        userId: `${userId}-caller`,
        identity: `test-caller-${session.id}`,
        leg: "client",
        handlers: {
          onStatusChanged: (s) => setLegStatus((p) => ({ ...p, client: s })),
          onError: (msg) => setMessage(`caller leg: ${msg}`),
        },
      });
      callerClientRef.current = callerClient;
      const calleeClient = await prepareBrowserVoiceClient({
        userId: `${userId}-callee`,
        identity: `test-callee-${session.id}`,
        leg: "pstn",
        handlers: {
          onStatusChanged: (s) => setLegStatus((p) => ({ ...p, pstn: s })),
          onError: (msg) => setMessage(`callee leg: ${msg}`),
        },
      });
      calleeClientRef.current = calleeClient;

      const callerSession = await callerClient.connect(session.id);
      callerSessionRef.current = callerSession;
      const calleeSession = await calleeClient.connect(session.id);
      calleeSessionRef.current = calleeSession;

      applyRole("caller", callerSession, calleeSession);
      setPhase("live");
      setMessage("Live. Tap the tabs to switch roles — only the active tab's mic is hot.");
    } catch (err) {
      setPhase("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }

  function applyRole(
    role: Role,
    callerSession: BrowserVoiceSession | null = callerSessionRef.current,
    calleeSession: BrowserVoiceSession | null = calleeSessionRef.current,
  ) {
    // Keep BOTH speakers always on — received audio on a leg plays as soon as it
    // arrives, so we can't disable the inactive speaker (audio would be lost
    // while muted). You'll hear both directions of translation, which is what
    // you want for testing anyway.
    if (callerSession) {
      callerSession.setMuted(role !== "caller");
      callerSession.setSpeakerEnabled(true);
    }
    if (calleeSession) {
      calleeSession.setMuted(role !== "callee");
      calleeSession.setSpeakerEnabled(true);
    }
  }

  function switchRole(role: Role) {
    if (phase !== "live") return;
    setActiveRole(role);
    applyRole(role);
  }

  async function teardown() {
    try {
      callerSessionRef.current?.destroy();
      calleeSessionRef.current?.destroy();
      callerClientRef.current?.destroy();
      calleeClientRef.current?.destroy();
    } catch {}
    callerSessionRef.current = null;
    calleeSessionRef.current = null;
    callerClientRef.current = null;
    calleeClientRef.current = null;
    const id = sessionIdRef.current;
    if (id) {
      try {
        await cancelCallSession(userId, id, "Ended test call");
      } catch {}
    }
  }

  async function hangUp() {
    setPhase("ending");
    setMessage("Ending call…");
    await teardown();
    onClose();
  }

  const canSwitch = phase === "live";

  return (
    <View style={styles.root}>
      <Text style={styles.title}>Test Call (mock, zero Twilio)</Text>
      <Text style={styles.subtitle}>
        Both legs open on this one device. The active tab's mic is hot; the other
        is muted. Both speakers stay on, so you'll hear every translation as it
        arrives — including your own voice translated to the other language on
        the opposite leg. That's useful for verifying each direction end-to-end.
      </Text>

      <View style={styles.tabs}>
        <Pressable
          onPress={() => switchRole("caller")}
          style={[
            styles.tab,
            activeRole === "caller" && styles.tabActive,
            !canSwitch && styles.tabDisabled,
          ]}
          disabled={!canSwitch}
        >
          <Text
            style={[
              styles.tabText,
              activeRole === "caller" && styles.tabTextActive,
            ]}
          >
            I am the caller ({callerLanguage.toUpperCase()})
          </Text>
        </Pressable>
        <Pressable
          onPress={() => switchRole("callee")}
          style={[
            styles.tab,
            activeRole === "callee" && styles.tabActive,
            !canSwitch && styles.tabDisabled,
          ]}
          disabled={!canSwitch}
        >
          <Text
            style={[
              styles.tabText,
              activeRole === "callee" && styles.tabTextActive,
            ]}
          >
            I am the callee ({calleeLanguage.toUpperCase()})
          </Text>
        </Pressable>
      </View>

      <View style={styles.statusBox}>
        <Text style={styles.statusLine}>phase: {phase}</Text>
        {sessionId ? (
          <Text selectable style={styles.statusMono}>
            session id: {sessionId}
          </Text>
        ) : null}
        <Text style={styles.statusLine}>
          active role: {activeRole}  •  mic: {activeRole === "caller" ? "caller (client leg)" : "callee (pstn leg)"}
        </Text>
        <Text style={styles.statusLine}>
          legs  •  client: {legStatus.client}  •  pstn: {legStatus.pstn}
        </Text>
        {message ? <Text style={styles.message}>{message}</Text> : null}
      </View>

      <View style={styles.footer}>
        {phase === "creating" || phase === "starting" || phase === "connecting" ? (
          <ActivityIndicator color="#fff" size="small" />
        ) : null}
        <Pressable onPress={hangUp} style={styles.endBtn}>
          <Text style={styles.endBtnText}>End call</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#111",
    padding: 20,
    gap: 14,
  },
  title: {
    color: "#fff",
    fontSize: 20,
    fontWeight: "700",
  },
  subtitle: {
    color: "#aaa",
    fontSize: 13,
    lineHeight: 18,
  },
  tabs: {
    flexDirection: "row",
    gap: 8,
    marginTop: 4,
  },
  tab: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
    backgroundColor: "#222",
    borderWidth: 2,
    borderColor: "transparent",
  },
  tabActive: {
    backgroundColor: "#1d3a6f",
    borderColor: "#3478f6",
  },
  tabDisabled: {
    opacity: 0.5,
  },
  tabText: {
    color: "#bbb",
    fontSize: 14,
    fontWeight: "600",
  },
  tabTextActive: {
    color: "#fff",
  },
  statusBox: {
    backgroundColor: "#1a1a1a",
    padding: 12,
    borderRadius: 8,
    gap: 4,
  },
  statusLine: {
    color: "#ccc",
    fontSize: 12,
  },
  statusMono: {
    color: "#9ad1ff",
    fontSize: 11,
    fontFamily: "monospace" as any,
  },
  message: {
    color: "#ffcb4b",
    fontSize: 12,
    marginTop: 4,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: "auto",
  },
  endBtn: {
    flex: 1,
    backgroundColor: "#d93b3b",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  endBtnText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
});
